import { StateMachine } from 'xstate';
import { createModel } from 'xstate/lib/model';
import { WorkflowContext, WorkflowEvent } from './state.types';
import { StateNamesEnum } from '../../enum/state-names-enum';

export function createBookingModel(initialCtx: WorkflowContext) {
    return createModel(initialCtx, {
        events: {
            UPDATE: (
                hasAssignment: boolean,
                hasParticipantsCount: boolean,
                mustAcceptTerms: boolean,
                mustAcceptEmailConsent: boolean,
                bookingTimeframe: number,
                serviceOnly: boolean,
                hasAppointmentType: boolean,
            ) => ({
                hasAssignment,
                hasParticipantsCount,
                mustAcceptTerms,
                mustAcceptEmailConsent,
                bookingTimeframe,
                serviceOnly,
                hasAppointmentType,
            }),
        },
    });
}

export type BookingModel = ReturnType<typeof createBookingModel>;

export function buildBookingMachine(
    userModel: BookingModel,
    opts: {
        initialState?: StateNamesEnum;
        storedConfig?: string | undefined;
        serviceOnlyFlag: boolean;
    },
) {
    const serviceOnly = opts.serviceOnlyFlag;

    let machineConfig: any = {
        id: 'booking',
        context: userModel.initialContext,
        initial: opts.initialState,
        predictableActionArguments: true,
        states: {
            // service_only -> api gibt keine forms zurück; state wird genommen sonst geskipped
            service_only: {
                on: {
                    UPDATE: [
                        {
                            actions: userModel.assign({
                                mustAcceptTerms: (_: any, event: any) => {
                                    return event.mustAcceptTerms;
                                },
                                mustAcceptEmailConsent: (_: any, event: any) => {
                                    return event.mustAcceptEmailConsent;
                                },
                                serviceOnly: (_: any, event: any) => {
                                    return event.serviceOnly;
                                },
                            }),
                        },
                    ],
                    NEXT: [
                        {
                            target: StateNamesEnum.DATE_FIRST,
                        },
                    ],
                },
                meta: {
                    step: 1,
                    breadcrumbName: StateNamesEnum.SERVICE_ONLY,
                    backFunction: 'backToServiceOnly',
                    isDynamic: true,
                    serviceOnly: serviceOnly,
                },
            },

            // service_only -> court_and_service wird geskipped
            court_and_service: {
                on: {
                    UPDATE: [
                        {
                            actions: userModel.assign({
                                hasAssignment: (_: any, event: any) => {
                                    return event.hasAssignment;
                                },
                                hasParticipantsCount: (_: any, event: any) => {
                                    return event.hasParticipantsCount;
                                },
                                mustAcceptTerms: (_: any, event: any) => {
                                    return event.mustAcceptTerms;
                                },
                                mustAcceptEmailConsent: (_: any, event: any) => {
                                    return event.mustAcceptEmailConsent;
                                },
                                bookingTimeframe: (_: any, event: any) => {
                                    return event.bookingTimeframe;
                                },
                                serviceOnly: (_: any, event: any) => {
                                    return event.serviceOnly;
                                },
                                hasAppointmentType: (_: any, event: any) => {
                                    return event.hasAppointmentType;
                                },
                            }),
                        },
                    ],
                    NEXT: [
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                        {
                            target: StateNamesEnum.ASSIGNMENT_STRATEGY,
                            cond: 'hasAssignment',
                        },
                        {
                            target: StateNamesEnum.PARTICIPANT_COUNT,
                            cond: 'hasParticipantsCount',
                        },
                        {
                            target: StateNamesEnum.DATE_FIRST,
                        },
                    ],
                },
                meta: {
                    step: 1,
                    breadcrumbName: StateNamesEnum.COURT_AND_SERVICE,
                    backFunction: 'backToCourtAndService',
                    isDynamic: true,
                },
            },

            appointment_type: {
                on: {
                    NEXT: [
                        {
                            target: StateNamesEnum.ASSIGNMENT_STRATEGY,
                            cond: 'hasAssignment',
                        },
                        {
                            target: StateNamesEnum.PARTICIPANT_COUNT,
                            cond: 'hasParticipantsCount',
                        },
                        {
                            target: StateNamesEnum.DATE_FIRST,
                            cond: '!hasParticipantsCount',
                        },
                    ],
                    BACK: [
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    BACK_TO_COURT_AND_SERVICE: [
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                },
                meta: {
                    step: 2,
                    breadcrumbName: 'dynamische_frontend_texte.terminart.breadcrumb_text',
                    backFunction: 'backToAppointmentType',
                },
            },

            // service_only -> assignment_strategy wird geskipped
            assignment_strategy: {
                on: {
                    NEXT: [
                        {
                            target: StateNamesEnum.PARTICIPANT_COUNT,
                            cond: 'hasParticipantsCount',
                        },
                        {
                            target: StateNamesEnum.DATE_FIRST,
                            cond: '!hasParticipantsCount',
                        },
                    ],
                    BACK: [
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    BACK_TO_COURT_AND_SERVICE: [
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    BACK_TO_APPOINTMENT_TYPE: [
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                    ],
                },
                meta: {
                    step: 3,
                    breadcrumbName: 'dynamische_frontend_texte.verteilmechanismen.html_content.name_breadcrumb_text',
                    backFunction: 'backToAssignmentStrategy',
                },
            },

            // service_only -> participant_count wird geskipped
            participant_count: {
                on: {
                    NEXT: [
                        {
                            target: StateNamesEnum.DATE_FIRST,
                        },
                    ],
                    BACK: [
                        {
                            target: StateNamesEnum.ASSIGNMENT_STRATEGY,
                            cond: 'hasAssignment',
                        },
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    BACK_TO_COURT_AND_SERVICE: [
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    BACK_TO_ASSIGNMENT_STRATEGY: [
                        {
                            target: StateNamesEnum.ASSIGNMENT_STRATEGY,
                            cond: 'hasAssignment',
                        },
                    ],
                    BACK_TO_APPOINTMENT_TYPE: [
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                    ],
                },
                meta: {
                    step: 4,
                    breadcrumbName: 'dynamische_frontend_texte.anzahl_teilnehmer.attribute_text.breadcrumb_text',
                    backFunction: 'backToParticipantsCount',
                },
            },
            date_first: {
                on: {
                    NEXT: [
                        {
                            target: StateNamesEnum.PERSONAL_DATA,
                        },
                    ],
                    BACK: [
                        {
                            target: StateNamesEnum.PARTICIPANT_COUNT,
                            cond: 'hasParticipantsCount',
                        },
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                        {
                            target: StateNamesEnum.ASSIGNMENT_STRATEGY,
                            cond: 'hasAssignment',
                        },
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    SWITCH_TO_TIME_FIRST: [
                        {
                            target: StateNamesEnum.TIME_FIRST,
                        },
                    ],
                    BACK_TO_SERVICE_ONLY: [
                        {
                            target: StateNamesEnum.SERVICE_ONLY,
                        },
                    ],
                    BACK_TO_COURT_AND_SERVICE: [
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    BACK_TO_APPOINTMENT_TYPE: [
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                    ],
                    BACK_TO_ASSIGNMENT_STRATEGY: [
                        {
                            target: StateNamesEnum.ASSIGNMENT_STRATEGY,
                            cond: 'hasAssignment',
                        },
                    ],
                    BACK_TO_PARTICIPANT_COUNT: [
                        {
                            target: StateNamesEnum.PARTICIPANT_COUNT,
                            cond: 'hasParticipantsCount',
                        },
                    ],
                },
                meta: {
                    step: 5,
                    breadcrumbName: 'dynamische_frontend_texte.datum_auswahl.html_content.breadcrumb_text',
                    backFunction: 'backToDateFirst',
                    isDynamic: true,
                },
            },

            // service_only -> time_first wird geskipped
            time_first: {
                on: {
                    NEXT: [
                        {
                            target: StateNamesEnum.PERSONAL_DATA,
                        },
                    ],
                    BACK: [
                        {
                            target: StateNamesEnum.DATE_FIRST,
                        },
                    ],
                    SWITCH_TO_DATE_FIRST: [
                        {
                            target: StateNamesEnum.DATE_FIRST,
                        },
                    ],
                    BACK_TO_COURT_AND_SERVICE: [
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    BACK_TO_APPOINTMENT_TYPE: [
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                    ],
                    BACK_TO_ASSIGNMENT_STRATEGY: [
                        {
                            target: StateNamesEnum.ASSIGNMENT_STRATEGY,
                            cond: 'hasAssignment',
                        },
                    ],
                    BACK_TO_PARTICIPANT_COUNT: [
                        {
                            target: StateNamesEnum.PARTICIPANT_COUNT,
                            cond: 'hasParticipantsCount',
                        },
                    ],
                },
                meta: {
                    step: 5,
                    breadcrumbName: 'dynamische_frontend_texte.zeit_auswahl.html_content.breadcrumb_text',
                    backFunction: 'backToDateFirst',
                    isDynamic: true,
                },
            },

            // service_only -> api bekommt nur generellen service
            // -> tage kommen zurück wie bisher
            // -> tag ist ausgewählt
            // -> api bekommt nur tag und generellen service
            // -> dabei wird court bestimmt
            // -> api für config wird erneut angefragt, dies mit court - service
            // -> Form kommen zurück
            personal_data: {
                on: {
                    NEXT: [
                        {
                            target: StateNamesEnum.SUMMARY,
                        },
                    ],
                    BACK: [
                        {
                            target: StateNamesEnum.DATE_FIRST,
                        },
                    ],
                    BACK_TO_SERVICE_ONLY: [
                        {
                            target: StateNamesEnum.SERVICE_ONLY,
                        },
                    ],
                    BACK_TO_COURT_AND_SERVICE: [
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    BACK_TO_APPOINTMENT_TYPE: [
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                    ],
                    BACK_TO_ASSIGNMENT_STRATEGY: [
                        {
                            target: StateNamesEnum.ASSIGNMENT_STRATEGY,
                            cond: 'hasAssignment',
                        },
                    ],
                    BACK_TO_PARTICIPANT_COUNT: [
                        {
                            target: StateNamesEnum.PARTICIPANT_COUNT,
                            cond: 'hasParticipantsCount',
                        },
                    ],
                    BACK_TO_DATE_FIRST: [
                        {
                            target: StateNamesEnum.DATE_FIRST,
                        },
                    ],
                },
                meta: {
                    step: 6,
                    breadcrumbName: 'dynamische_frontend_texte.breadcrumb.html_content.persoenliche_daten',
                    backFunction: 'backToPersonalData',
                },
            },
            summary: {
                on: {
                    NEXT: [
                        {
                            target: StateNamesEnum.THANK_YOU,
                        },
                    ],
                    BACK: [
                        {
                            target: StateNamesEnum.PERSONAL_DATA,
                        },
                    ],
                    BACK_TO_SERVICE_ONLY: [
                        {
                            target: StateNamesEnum.SERVICE_ONLY,
                        },
                    ],
                    BACK_TO_COURT_AND_SERVICE: [
                        {
                            target: StateNamesEnum.COURT_AND_SERVICE,
                        },
                    ],
                    BACK_TO_APPOINTMENT_TYPE: [
                        {
                            target: StateNamesEnum.APPOINTMENT_TYPE,
                            cond: 'hasAppointmentType',
                        },
                    ],
                    BACK_TO_ASSIGNMENT_STRATEGY: [
                        {
                            target: StateNamesEnum.ASSIGNMENT_STRATEGY,
                            cond: 'hasAssignment',
                        },
                    ],
                    BACK_TO_PARTICIPANT_COUNT: [
                        {
                            target: StateNamesEnum.PARTICIPANT_COUNT,
                            cond: 'hasParticipantsCount',
                        },
                    ],
                    BACK_TO_DATE_FIRST: [
                        {
                            target: StateNamesEnum.DATE_FIRST,
                        },
                    ],
                    BACK_TO_PERSONAL_DATA: [
                        {
                            target: StateNamesEnum.PERSONAL_DATA,
                        },
                    ],
                    BACK_TO_SUMMARY: [
                        {
                            target: StateNamesEnum.SUMMARY,
                        },
                    ],
                },
                meta: {
                    step: 7,
                    breadcrumbName: 'dynamische_frontend_texte.breadcrumb.html_content.zusammenfassung',
                    backFunction: 'backToSummary',
                },
            },
            thank_you: {
                type: 'final',
                meta: {
                    step: 8,
                    breadcrumbName: 'dynamische_frontend_texte.breadcrumb.html_content.zusammenfassung',
                },
            },
        },
        entry: userModel.assign({
            hasAssignment: true,
            hasParticipantsCount: true,
            mustAcceptTerms: true,
            mustAcceptEmailConsent: true,
            bookingTimeframe: 3,
            serviceOnly: false,
            hasAppointmentType: false,
        }),
    };

    if (opts.storedConfig) {
        try {
            machineConfig = JSON.parse(opts.storedConfig);
            machineConfig.predictableActionArguments = true;
            machineConfig.entry[0].assignment = machineConfig.context;
            machineConfig.entry.assignment = machineConfig.context;
        } catch (e) {
            console.error('Corrupted machine config object found');
            console.error(e);
        }
    }

    machineConfig.initial = opts.initialState;

    return userModel.createMachine(machineConfig, {
        guards: {
            hasAssignment: (context: any) => context.hasAssignment === true,
            hasAppointmentType: (context: any) => context.hasAppointmentType === true,
            hasParticipantsCount: (context: any) => context.hasParticipantsCount === true,
            '!hasParticipantsCount': (context: any) => context.hasParticipantsCount === false,
        },
    }) as StateMachine<WorkflowContext, any, WorkflowEvent>;
}
